import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import {
  Sparkles, ListChecks, UserRound, Briefcase, FileDown, Layers,
  Menu, X,
} from 'lucide-react';
import { cn } from '../lib/cn.js';
import UserMenu from './UserMenu.jsx';

const navItems = [
  { to: '/evaluate', label: '评估 JD', icon: Sparkles },
  { to: '/batch', label: '批量评估', icon: Layers },
  { to: '/tracker', label: '应用追踪', icon: ListChecks },
  { to: '/profile', label: '我的简历', icon: UserRound },
  { to: '/resume', label: 'PDF 导出', icon: FileDown },
];

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // 路由切换自动关抽屉
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // 抽屉打开时锁定 body 滚动 + ESC 关闭
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <header className="sticky top-0 z-30 border-b border-ink-200 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 font-semibold text-ink-900">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent-500 text-white">
              <Briefcase className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span>careerops</span>
            <span className="hidden text-xs font-normal text-ink-400 sm:inline">· 智能求职助手</span>
          </Link>

          <div className="flex items-center gap-1 sm:gap-2">
            <nav className="hidden items-center gap-1 sm:flex">
              {navItems.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition',
                        isActive
                          ? 'bg-accent-50 text-accent-700'
                          : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <UserMenu />

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100 sm:hidden"
              aria-label="打开导航菜单"
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav-drawer"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* 移动端抽屉导航 */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 sm:hidden"
          role="dialog"
          aria-modal="true"
          id="mobile-nav-drawer"
        >
          <div
            className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full w-72 max-w-[85%] flex-col bg-white shadow-xl">
            <div className="flex h-14 items-center justify-between border-b border-ink-200 px-4">
              <span className="font-semibold text-ink-900">导航</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100"
                aria-label="关闭导航菜单"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3">
              <ul className="space-y-1">
                {navItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
                            isActive
                              ? 'bg-accent-50 text-accent-700'
                              : 'text-ink-700 hover:bg-ink-100',
                          )
                        }
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className="border-t border-ink-200 p-3">
              <Link
                to="/evaluate"
                className="btn-primary w-full justify-center"
              >
                <Sparkles className="h-4 w-4" />
                立即开始评估
              </Link>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">
        <Outlet />
      </main>

      <footer className="border-t border-ink-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-xs text-ink-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <span className="font-medium text-ink-700">careerops</span>{' '}
            · v0.5 · 由 SiliconFlow 提供 AI 推理
          </div>
          <div className="space-x-3">
            <a
              href="https://github.com/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ink-700"
            >
              GitHub
            </a>
            <span>·</span>
            <span>登录后云端同步 · 未登录纯本地</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
